import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useJobListings } from '@/hooks/useJobListings';
import { supabase } from '@/lib/supabase';

export default function CareersPage() {
  const { jobs, departments } = useJobListings();
  const [filter, setFilter] = useState('All Departments');
  const [selectedJob, setSelectedJob] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', linkedin: '', coverLetter: '', cvFileName: '' });
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');
  const [cvFile, setCvFile] = useState<File | null>(null);

  // Speculative application state
  const [showSpeculativeForm, setShowSpeculativeForm] = useState(false);
  const [specData, setSpecData] = useState({ name: '', email: '', phone: '', linkedin: '', cvFileName: '', message: '' });
  const [specStatus, setSpecStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [specMessage, setSpecMessage] = useState('');
  const [specCvFile, setSpecCvFile] = useState<File | null>(null);

  const jobListings = jobs;

  const filteredJobs = filter === 'All Departments'
    ? jobs
    : jobs.filter((j) => j.department === filter);

  const handleApply = (jobId: string) => {
    setSelectedJob(jobId);
    setShowForm(true);
    setFormStatus('idle');
    setFormMessage('');
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormStatus('submitting');

    const target = e.currentTarget as HTMLFormElement;
    const fd = new FormData(target);

    const honeypot = (fd.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMessage('Your application has been submitted successfully.');
      return;
    }

    fd.delete('phone_alt');

    const applicationData = {
      position: (fd.get('position') as string) || 'Job Application',
      name: fd.get('name') as string,
      email: fd.get('email') as string,
      phone: (fd.get('phone') as string) || null,
      linkedin: (fd.get('linkedin') as string) || null,
      cover_letter: (fd.get('cover_letter') as string) || null,
      cv_filename: formData.cvFileName || null,
    };

    // Save to Supabase first — this is the primary storage for admin panel
    let supabaseError = false;
    try {
      const { error } = await supabase.from('career_applications').insert(applicationData);
      if (error) {
        console.error('Supabase career insert error:', error);
        supabaseError = true;
      }
    } catch (err) {
      console.error('Supabase career insert exception:', err);
      supabaseError = true;
    }

    // Also submit to Readdy form API as secondary
    try {
      const res = await fetch('https://readdy.ai/api/form/d9fr0k4ugrdi4rjg50m0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(fd as unknown as Record<string, string>).toString(),
      });

      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (!res.ok || parsed?.code !== 'OK') {
        console.warn('Readdy form API returned non-OK:', responseText);
      }
    } catch (err) {
      console.warn('Readdy form API call failed:', err);
    }

    if (supabaseError) {
      setFormStatus('error');
      setFormMessage('Something went wrong saving your application. Please try again or contact us directly.');
    } else {
      setFormStatus('success');
      setFormMessage('Your application has been submitted successfully.');
      setFormData({ name: '', email: '', phone: '', linkedin: '', coverLetter: '', cvFileName: '' });
      setCvFile(null);
    }
  };

  const handleSpeculativeSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSpecStatus('submitting');

    const target = e.currentTarget as HTMLFormElement;
    const fd = new FormData(target);

    const honeypot = (fd.get('mobile_alt') as string || '').trim();
    if (honeypot) {
      setSpecStatus('success');
      setSpecMessage('Your speculative application has been submitted.');
      return;
    }

    fd.delete('mobile_alt');

    const applicationData = {
      position: (fd.get('position') as string) || 'Speculative Application',
      name: fd.get('name') as string,
      email: fd.get('email') as string,
      phone: (fd.get('phone') as string) || null,
      linkedin: (fd.get('linkedin') as string) || null,
      message: (fd.get('message') as string) || null,
      cv_filename: specData.cvFileName || null,
    };

    // Save to Supabase first — this is the primary storage for admin panel
    let supabaseError = false;
    try {
      const { error } = await supabase.from('career_applications').insert(applicationData);
      if (error) {
        console.error('Supabase career insert error:', error);
        supabaseError = true;
      }
    } catch (err) {
      console.error('Supabase career insert exception:', err);
      supabaseError = true;
    }

    // Also submit to Readdy form API as secondary
    try {
      const res = await fetch('https://readdy.ai/api/form/d9fr0k4ugrdi4rjg50m0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(fd as unknown as Record<string, string>).toString(),
      });

      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (!res.ok || parsed?.code !== 'OK') {
        console.warn('Readdy form API returned non-OK:', responseText);
      }
    } catch (err) {
      console.warn('Readdy form API call failed:', err);
    }

    if (supabaseError) {
      setSpecStatus('error');
      setSpecMessage('Something went wrong saving your application. Please try again or contact us directly.');
    } else {
      setSpecStatus('success');
      setSpecMessage('Your speculative application has been submitted.');
      setSpecData({ name: '', email: '', phone: '', linkedin: '', cvFileName: '', message: '' });
      setSpecCvFile(null);
    }
  };

  return (
    <div className="bg-background-50">
      {/* Hero */}
      <section className="pt-24 md:pt-32 pb-12 md:pb-16 px-4 md:px-8 lg:px-12">
        <div className="max-w-3xl mx-auto text-center">
          <span className="inline-block font-label text-xs tracking-[0.2em] uppercase text-primary-400 mb-3">
            / Join Our Team
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-bold text-foreground-50 leading-tight mb-5">
            Careers at <span className="text-primary-400">TMT</span>
          </h1>
          <p className="text-foreground-300 text-base md:text-lg leading-relaxed max-w-2xl mx-auto font-body mb-10">
            Build the future of digital finance across the Middle East, Africa, and Europe. We are looking for talented, mission-driven people to join our team.
          </p>

          {/* Hero Banner Image */}
          <div className="relative overflow-hidden rounded-2xl border border-primary-700/20">
            <div className="absolute inset-0 bg-gradient-to-t from-background-50/50 via-transparent to-transparent z-10" />
            <img
              src="https://readdy.ai/api/search-image?query=Vibrant%20diverse%20professional%20team%20in%20modern%20collaborative%20workspace%20natural%20light%20streaming%20through%20floor%20to%20ceiling%20windows%20authentic%20workplace%20culture%20warm%20and%20energetic%20atmosphere%20creative%20fintech%20office%20environment&width=1400&height=500&seq=careers-hero-10&orientation=landscape"
              alt="TMT Careers — Join Our Global Team"
              className="w-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Culture Section */}
      <section className="py-12 md:py-16 px-4 md:px-8 lg:px-12 bg-background-100">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {[
              { icon: 'ri-global-line', title: '3 Continents, One Team', desc: 'Our team spans cultures, languages, and time zones across the Middle East, Africa, and Europe — united by a shared mission to make finance borderless.' },
              { icon: 'ri-lightbulb-flash-line', title: 'Build What Matters', desc: 'Every line of code, every product decision, and every customer interaction directly impacts how millions of people move, manage, and grow their money.' },
              { icon: 'ri-heart-pulse-line', title: 'People First', desc: 'Competitive compensation, equity packages, flexible remote work, 28 days of annual leave, comprehensive health coverage, and an annual learning stipend of USD 2,500.' },
            ].map((item) => (
              <div key={item.title} className="p-6 md:p-8 bg-background-50 border border-primary-800/20 rounded-xl text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center border border-primary-600/30 rounded-full">
                  <i className={`${item.icon} text-primary-400 text-2xl`} />
                </div>
                <h3 className="font-heading text-lg text-foreground-100 mb-2">{item.title}</h3>
                <p className="text-foreground-400 text-sm leading-relaxed font-body">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Culture Image */}
          <div className="mt-10 relative overflow-hidden rounded-2xl border border-primary-700/20">
            <div className="absolute inset-0 bg-gradient-to-r from-background-100/60 via-transparent to-background-100/60 z-10" />
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20office%20workspace%20with%20standing%20desks%20and%20collaborative%20areas%20warm%20natural%20lighting%20plants%20and%20elegant%20decor%20premium%20fintech%20company%20culture%20environment%20wide%20angle%20architectural%20photography&width=1400&height=450&seq=careers-culture-11&orientation=landscape"
              alt="TMT Office — Modern Collaborative Workspace"
              className="w-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Job Listings */}
      <section className="py-12 md:py-16 px-4 md:px-8 lg:px-12">
        <div className="max-w-5xl mx-auto">
          <span className="inline-block font-label text-xs tracking-[0.2em] uppercase text-primary-400 mb-6">
            / Open Positions
          </span>

          {/* Filter */}
          <div className="flex flex-wrap gap-2 mb-8">
            {departments.map((dept) => (
              <button
                key={dept}
                onClick={() => setFilter(dept)}
                className={`px-4 py-2 rounded-full font-label text-xs tracking-wider transition-all duration-300 ${
                  filter === dept
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 border border-primary-800/20 text-foreground-400 hover:text-foreground-200 hover:border-primary-600/30'
                }`}
              >
                {dept}
              </button>
            ))}
          </div>

          {/* Job Cards */}
          <div className="space-y-4">
            {filteredJobs.map((job) => (
              <div
                key={job.id}
                className="p-5 md:p-6 bg-background-100 border border-primary-800/20 rounded-xl hover:border-primary-600/30 transition-colors"
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center border border-primary-600/30 rounded-lg">
                      <i className={`${job.icon} text-primary-400 text-lg`} />
                    </div>
                    <div>
                      <h3 className="font-heading text-lg text-foreground-100 mb-1">{job.title}</h3>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-foreground-500 text-xs font-label">
                        <span className="flex items-center gap-1"><i className="ri-building-line" /> {job.department}</span>
                        <span className="flex items-center gap-1"><i className="ri-map-pin-line" /> {job.location}</span>
                        <span className="px-2 py-0.5 bg-primary-500/10 text-primary-400 rounded-full text-[0.65rem]">{job.type}</span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleApply(job.id)}
                    className="px-5 py-2.5 bg-primary-500 text-background-50 rounded-full font-label text-sm tracking-wider hover:bg-primary-400 transition-colors whitespace-nowrap flex-shrink-0"
                  >
                    Apply Now
                  </button>
                </div>
                {/* Expanded Description */}
                {selectedJob === job.id && !showForm && (
                  <div className="mt-5 pt-5 border-t border-primary-800/20">
                    <p className="text-foreground-400 text-sm leading-relaxed mb-4 font-body">{job.description}</p>
                    <h4 className="font-heading text-foreground-200 text-sm font-semibold mb-2">Requirements</h4>
                    <ul className="space-y-1.5 mb-4">
                      {job.requirements.map((req) => (
                        <li key={req} className="flex gap-2 text-foreground-400 text-sm font-body">
                          <span className="text-primary-400 mt-1 flex-shrink-0">&bull;</span>
                          <span>{req}</span>
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={() => handleApply(job.id)}
                      className="px-5 py-2.5 bg-primary-500 text-background-50 rounded-full font-label text-sm tracking-wider hover:bg-primary-400 transition-colors whitespace-nowrap"
                    >
                      Apply for this Position
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {filteredJobs.length === 0 && (
            <div className="text-center py-12">
              <p className="text-foreground-500 text-sm font-body">No open positions in this department at the moment. Check back soon or send us a speculative application.</p>
            </div>
          )}
        </div>
      </section>

      {/* Application Form Modal */}
      {showForm && (
        <section className="py-12 md:py-16 px-4 md:px-8 lg:px-12 bg-background-100">
          <div className="max-w-2xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <span className="inline-block font-label text-xs tracking-[0.2em] uppercase text-primary-400 mb-1">
                  / Application
                </span>
                <h2 className="font-heading text-2xl text-foreground-50">
                  {jobListings.find((j) => j.id === selectedJob)?.title || 'Job Application'}
                </h2>
              </div>
              <button
                onClick={() => { setShowForm(false); setSelectedJob(null); }}
                className="w-10 h-10 flex items-center justify-center border border-primary-700/30 rounded-full text-foreground-400 hover:text-foreground-100 hover:border-primary-500/40 transition-colors"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            {formStatus === 'success' ? (
              <div className="bg-background-50 border border-primary-600/30 rounded-xl p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center border border-primary-500/40 rounded-full">
                  <i className="ri-check-line text-primary-400 text-3xl" />
                </div>
                <h3 className="font-heading text-xl text-foreground-100 mb-2">Application Submitted</h3>
                <p className="text-foreground-400 text-sm leading-relaxed font-body">{formMessage}</p>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubmit} className="bg-background-50 border border-primary-800/20 rounded-xl p-6 md:p-8 space-y-5">
                {/* Honeypot */}
                <div className="hp-field">
                  <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                </div>

                <input type="hidden" name="position" value={jobListings.find((j) => j.id === selectedJob)?.title || 'Job Application'} />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-foreground-300 text-xs font-label tracking-wider uppercase mb-2">Full Name</label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-3 bg-background-100 border border-primary-700/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label className="block text-foreground-300 text-xs font-label tracking-wider uppercase mb-2">Email Address</label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 bg-background-100 border border-primary-700/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                      placeholder="you@example.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-foreground-300 text-xs font-label tracking-wider uppercase mb-2">Phone Number</label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-4 py-3 bg-background-100 border border-primary-700/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                      placeholder="+961 00 000 000"
                    />
                  </div>
                  <div>
                    <label className="block text-foreground-300 text-xs font-label tracking-wider uppercase mb-2">LinkedIn Profile</label>
                    <input
                      type="url"
                      name="linkedin"
                      value={formData.linkedin}
                      onChange={(e) => setFormData({ ...formData, linkedin: e.target.value })}
                      className="w-full px-4 py-3 bg-background-100 border border-primary-700/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                      placeholder="https://linkedin.com/in/yourprofile"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-foreground-300 text-xs font-label tracking-wider uppercase mb-2">Upload CV / Resume</label>
                  <label className="flex flex-col items-center justify-center w-full px-4 py-6 bg-background-100 border border-dashed border-primary-700/30 rounded-lg cursor-pointer hover:border-primary-500/50 transition-colors group">
                    {cvFile ? (
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 flex items-center justify-center bg-primary-500/20 rounded-lg">
                          <i className="ri-file-pdf-line text-primary-400 text-lg" />
                        </div>
                        <div className="text-left">
                          <p className="text-foreground-100 text-sm font-body font-medium">{cvFile.name}</p>
                          <p className="text-foreground-500 text-xs font-body">{(cvFile.size / 1024).toFixed(1)} KB</p>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); e.preventDefault(); setCvFile(null); setFormData({ ...formData, cvFileName: '' }); }}
                          className="w-6 h-6 flex items-center justify-center rounded-full bg-background-50 border border-primary-700/30 hover:border-red-400 text-foreground-400 hover:text-red-400 transition-colors"
                        >
                          <i className="ri-close-line text-xs" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="w-12 h-12 flex items-center justify-center border border-primary-600/30 rounded-full mb-2 group-hover:border-primary-400 transition-colors">
                          <i className="ri-upload-cloud-2-line text-primary-400 text-xl" />
                        </div>
                        <p className="text-foreground-400 text-xs font-body mb-1">Click to upload your CV</p>
                        <p className="text-foreground-600 text-[0.6rem] font-body">PDF, DOC, DOCX — Max 10MB</p>
                      </>
                    )}
                    <input
                      type="file"
                      name="cv_file"
                      accept=".pdf,.doc,.docx"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setCvFile(file);
                          setFormData({ ...formData, cvFileName: file.name });
                        }
                      }}
                    />
                  </label>
                </div>

                <div>
                  <label className="block text-foreground-300 text-xs font-label tracking-wider uppercase mb-2">Cover Letter</label>
                  <textarea
                    name="cover_letter"
                    rows={5}
                    maxLength={500}
                    required
                    value={formData.coverLetter}
                    onChange={(e) => setFormData({ ...formData, coverLetter: e.target.value })}
                    className="w-full px-4 py-3 bg-background-100 border border-primary-700/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors resize-none"
                    placeholder="Tell us why you are interested in this role and what makes you a great fit. (Max 500 characters)"
                  />
                  <p className="text-foreground-600 text-xs mt-1 font-body">{formData.coverLetter.length}/500</p>
                </div>

                {formStatus === 'error' && (
                  <p className="text-red-400 text-sm font-body">{formMessage}</p>
                )}

                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-full py-3.5 bg-primary-500 text-background-50 rounded-full font-label text-sm tracking-wider hover:bg-primary-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                >
                  {formStatus === 'submitting' ? 'Submitting...' : 'Submit Application'}
                </button>
              </form>
            )}
          </div>
        </section>
      )}

      {/* Speculative Application CTA / Form */}
      {!showForm && (
        <section className="py-12 md:py-16 px-4 md:px-8 lg:px-12 bg-background-100">
          <div className="max-w-xl mx-auto text-center">
            {!showSpeculativeForm ? (
              <>
                <div className="w-16 h-16 mx-auto mb-5 flex items-center justify-center border border-primary-600/30 rounded-full">
                  <i className="ri-mail-send-line text-primary-400 text-2xl" />
                </div>
                <h3 className="font-heading text-2xl text-foreground-100 mb-3">Do Not See the Right Role?</h3>
                <p className="text-foreground-400 text-sm leading-relaxed mb-6 font-body">
                  We are always looking for exceptional talent. Send us your resume and tell us how you can contribute to TMT&apos;s mission — we will keep your profile on file for future opportunities.
                </p>
                <button
                  onClick={() => setShowSpeculativeForm(true)}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full font-label text-sm tracking-wider hover:bg-primary-400 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Send Speculative Application
                  <i className="ri-arrow-right-line" />
                </button>
              </>
            ) : (
              <div className="text-left">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <span className="inline-block font-label text-xs tracking-[0.2em] uppercase text-primary-400 mb-1">
                      / Speculative Application
                    </span>
                    <h2 className="font-heading text-xl text-foreground-50">Tell Us About Yourself</h2>
                  </div>
                  <button
                    onClick={() => { setShowSpeculativeForm(false); setSpecStatus('idle'); }}
                    className="w-10 h-10 flex items-center justify-center border border-primary-700/30 rounded-full text-foreground-400 hover:text-foreground-100 hover:border-primary-500/40 transition-colors"
                  >
                    <i className="ri-close-line text-lg" />
                  </button>
                </div>

                {specStatus === 'success' ? (
                  <div className="bg-background-50 border border-primary-600/30 rounded-xl p-8 text-center">
                    <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center border border-primary-500/40 rounded-full">
                      <i className="ri-check-line text-primary-400 text-3xl" />
                    </div>
                    <h3 className="font-heading text-xl text-foreground-100 mb-2">Profile Received</h3>
                    <p className="text-foreground-400 text-sm leading-relaxed font-body">{specMessage}</p>
                  </div>
                ) : (
                  <form data-readdy-form onSubmit={handleSpeculativeSubmit} className="bg-background-50 border border-primary-800/20 rounded-xl p-6 md:p-8 space-y-5">
                    {/* Honeypot */}
                    <div className="hp-field">
                      <input type="text" name="mobile_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                    </div>

                    <input type="hidden" name="position" value="Speculative Application" />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-foreground-300 text-xs font-label tracking-wider uppercase mb-2">Full Name</label>
                        <input
                          type="text"
                          name="name"
                          required
                          value={specData.name}
                          onChange={(e) => setSpecData({ ...specData, name: e.target.value })}
                          className="w-full px-4 py-3 bg-background-100 border border-primary-700/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                          placeholder="Your full name"
                        />
                      </div>
                      <div>
                        <label className="block text-foreground-300 text-xs font-label tracking-wider uppercase mb-2">Email Address</label>
                        <input
                          type="email"
                          name="email"
                          required
                          value={specData.email}
                          onChange={(e) => setSpecData({ ...specData, email: e.target.value })}
                          className="w-full px-4 py-3 bg-background-100 border border-primary-700/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                          placeholder="you@example.com"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-foreground-300 text-xs font-label tracking-wider uppercase mb-2">Phone Number</label>
                        <input
                          type="tel"
                          name="phone"
                          required
                          value={specData.phone}
                          onChange={(e) => setSpecData({ ...specData, phone: e.target.value })}
                          className="w-full px-4 py-3 bg-background-100 border border-primary-700/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                          placeholder="+961 00 000 000"
                        />
                      </div>
                      <div>
                        <label className="block text-foreground-300 text-xs font-label tracking-wider uppercase mb-2">LinkedIn Profile</label>
                        <input
                          type="url"
                          name="linkedin"
                          value={specData.linkedin}
                          onChange={(e) => setSpecData({ ...specData, linkedin: e.target.value })}
                          className="w-full px-4 py-3 bg-background-100 border border-primary-700/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                          placeholder="https://linkedin.com/in/yourprofile"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-foreground-300 text-xs font-label tracking-wider uppercase mb-2">Upload CV / Resume</label>
                      <label className="flex flex-col items-center justify-center w-full px-4 py-6 bg-background-100 border border-dashed border-primary-700/30 rounded-lg cursor-pointer hover:border-primary-500/50 transition-colors group">
                        {specCvFile ? (
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 flex items-center justify-center bg-primary-500/20 rounded-lg">
                              <i className="ri-file-pdf-line text-primary-400 text-lg" />
                            </div>
                            <div className="text-left">
                              <p className="text-foreground-100 text-sm font-body font-medium">{specCvFile.name}</p>
                              <p className="text-foreground-500 text-xs font-body">{(specCvFile.size / 1024).toFixed(1)} KB</p>
                            </div>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); e.preventDefault(); setSpecCvFile(null); setSpecData({ ...specData, cvFileName: '' }); }}
                              className="w-6 h-6 flex items-center justify-center rounded-full bg-background-50 border border-primary-700/30 hover:border-red-400 text-foreground-400 hover:text-red-400 transition-colors"
                            >
                              <i className="ri-close-line text-xs" />
                            </button>
                          </div>
                        ) : (
                          <>
                            <div className="w-12 h-12 flex items-center justify-center border border-primary-600/30 rounded-full mb-2 group-hover:border-primary-400 transition-colors">
                              <i className="ri-upload-cloud-2-line text-primary-400 text-xl" />
                            </div>
                            <p className="text-foreground-400 text-xs font-body mb-1">Click to upload your CV</p>
                            <p className="text-foreground-600 text-[0.6rem] font-body">PDF, DOC, DOCX — Max 10MB</p>
                          </>
                        )}
                        <input
                          type="file"
                          name="cv_file"
                          accept=".pdf,.doc,.docx"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              setSpecCvFile(file);
                              setSpecData({ ...specData, cvFileName: file.name });
                            }
                          }}
                        />
                      </label>
                    </div>

                    <div>
                      <label className="block text-foreground-300 text-xs font-label tracking-wider uppercase mb-2">How Can You Contribute?</label>
                      <textarea
                        name="message"
                        rows={4}
                        maxLength={500}
                        required
                        value={specData.message}
                        onChange={(e) => setSpecData({ ...specData, message: e.target.value })}
                        className="w-full px-4 py-3 bg-background-100 border border-primary-700/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors resize-none"
                        placeholder="Tell us about your background, what type of role you are looking for, and how you can add value to TMT. (Max 500 characters)"
                      />
                      <p className="text-foreground-600 text-xs mt-1 font-body">{specData.message.length}/500</p>
                    </div>

                    {specStatus === 'error' && (
                      <p className="text-red-400 text-sm font-body">{specMessage}</p>
                    )}

                    <button
                      type="submit"
                      disabled={specStatus === 'submitting'}
                      className="w-full py-3.5 bg-primary-500 text-background-50 rounded-full font-label text-sm tracking-wider hover:bg-primary-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                    >
                      {specStatus === 'submitting' ? 'Submitting...' : 'Submit Speculative Application'}
                    </button>
                  </form>
                )}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Admin access — subtle link */}
      <div className="pb-8 text-center">
        <Link to="/admin/careers" className="inline-flex items-center gap-1.5 text-foreground-600 hover:text-primary-400 transition-colors text-xs font-body">
          <i className="ri-lock-line text-[0.65rem]" />
          Admin
        </Link>
      </div>
    </div>
  );
}